/**
 * T2 in-theme navigation — mobile drawer stitcher.
 *
 * Why this exists: the nav is authored ONCE as nested theme blocks
 * (_nav-menu → _nav-group → _nav-link). Shopify renders a block's
 * children exactly once ({% content_for 'blocks' %} may appear only once
 * per file — platform validation), so a single block tree cannot
 * server-render two differently-shaped DOMs (desktop mega menu + mobile
 * drawer). Instead each block server-renders its desktop markup AND an
 * inert <template data-navdrawer="..."> holding its drawer fragment,
 * authored in Liquid with the exact classes/structure of
 * snippets/header-drawer.liquid. This script only STITCHES those
 * fragments together — it generates no markup of its own, so drawer
 * styling/behavior stays byte-compatible with the stock drawer.
 * Horizon's on:click handlers are document-level delegated
 * (assets/component.js), so late-inserted nodes behave identically.
 *
 * Resilience notes (each earned by a real failure):
 *  • The desktop items are slotted light-DOM children of the
 *    <overflow-list> declarative-shadow-DOM component — there is NO
 *    .menu-list__list element in the served DOM, so we anchor on the
 *    <template data-navdrawer="menu"> elements themselves.
 *  • Horizon re-renders the header via the Section Rendering API after
 *    load (two-pass "eager loading"), REPLACING the drawer with a fresh
 *    unstitched copy. So stitching must be re-runnable: we watch for any
 *    pending drawer root via MutationObserver and also re-stitch on
 *    every drawer-open interaction. The per-root "done" marker makes
 *    repeat calls no-ops.
 */
(function () {
  'use strict';

  function stitchRoot(root) {
    var headerMenu = document.querySelector('header-menu');
    if (!headerMenu) return false;

    var menuTpls = headerMenu.querySelectorAll('template[data-navdrawer="menu"]');
    if (!menuTpls.length) return false;

    var animIndex = 0;
    menuTpls.forEach(function (menuTpl) {
      var item = menuTpl.closest('.menu-list__list-item') || menuTpl.parentElement;

      animIndex += 1;
      var li = document.createElement('li');
      li.className = 'menu-drawer__list-item';
      li.style.setProperty('--menu-drawer-animation-index', animIndex);
      li.appendChild(menuTpl.content.cloneNode(true));
      root.appendChild(li);

      var menuSlot = li.querySelector('[data-navdrawer-slot]');
      if (!menuSlot) return;

      // Groups: each desktop mega-menu column owns one group template.
      item.querySelectorAll('.mega-menu__column').forEach(function (col) {
        var groupTpl = null;
        for (var i = 0; i < col.children.length; i++) {
          if (col.children[i].matches('template[data-navdrawer="group"]')) groupTpl = col.children[i];
        }
        if (!groupTpl) return;

        if (groupTpl.hasAttribute('data-headerless')) {
          // Headerless group (no heading, e.g. COLLABORATIONS): its links
          // sit directly in the menu's drawer list as --parent rows, the
          // way the linklist drawer rendered second-level leaf items.
          col.querySelectorAll('template[data-navdrawer="link"]').forEach(function (linkTpl) {
            var frag = linkTpl.content.cloneNode(true);
            var a = frag.querySelector('a');
            if (a) a.className = a.className.replace('menu-drawer__menu-item--child', 'menu-drawer__menu-item--parent');
            menuSlot.appendChild(frag);
          });
          return;
        }

        var groupLi = document.createElement('li');
        groupLi.className = 'menu-drawer__list-item ' +
          (groupTpl.hasAttribute('data-flat') ? 'menu-drawer__list-item--flat' : 'menu-drawer__list-item--deep');
        groupLi.appendChild(groupTpl.content.cloneNode(true));
        menuSlot.appendChild(groupLi);

        var groupSlot = groupLi.querySelector('[data-navdrawer-slot]');
        if (!groupSlot) return;

        var linkIndex = 0;
        col.querySelectorAll('template[data-navdrawer="link"]').forEach(function (linkTpl) {
          linkIndex += 1;
          var frag = linkTpl.content.cloneNode(true);
          var linkLi = frag.querySelector('li');
          if (linkLi) linkLi.style.setProperty('--menu-drawer-animation-index', linkIndex);
          groupSlot.appendChild(frag);
        });
      });

      // Hero cards (opt-in per Menu hero carousel block: "Show cards in
      // mobile menu"). The desktop carousel can be the menu's FULL list
      // (COLLABORATIONS: 40+ cards vs a 9-link sidebar), and nothing else
      // carries those cards to mobile. Each card's fragment is appended
      // after the groups, in carousel order, unless a link with the same
      // title or same URL is already in this menu's drawer list.
      var heroPane = item.querySelector('.mega-menu__content--hero[data-navdrawer-hero]');
      if (!heroPane) return;
      if (heroPane.getAttribute('data-navdrawer-hero') === 'grid') {
        stitchHeroGrid(heroPane, menuSlot);
        return;
      }
      var seen = {};
      menuSlot.querySelectorAll('a[href]').forEach(function (a) {
        seen['t:' + normTitle(a.textContent)] = true;
        seen['u:' + normUrl(a.getAttribute('href'))] = true;
      });
      heroPane.querySelectorAll('template[data-navdrawer="hero-card"]').forEach(function (cardTpl) {
        var frag = cardTpl.content.cloneNode(true);
        var a = frag.querySelector('a[href]');
        if (!a) return;
        var tKey = 't:' + normTitle(a.textContent);
        var uKey = 'u:' + normUrl(a.getAttribute('href'));
        if (seen[tKey] || (uKey !== 'u:' && seen[uKey])) return;
        seen[tKey] = true;
        seen[uKey] = true;
        menuSlot.appendChild(frag);
      });
    });

    return true;
  }

  // "Image grid" mode: every card becomes a 2-up image tile, in carousel
  // order (so mobile order == desktop carousel order). Menu links that
  // duplicate a card (same title or URL) are dropped — the tile
  // supersedes them; other menu links stay as text rows above the grid.
  // Tiles past the block's limit start hidden behind a Show more button.
  function stitchHeroGrid(heroPane, menuSlot) {
    var heroTpl = heroPane.querySelector('template[data-navdrawer="hero"]');
    if (!heroTpl) return;
    var tileTpls = heroPane.querySelectorAll('template[data-navdrawer="hero-tile"]');
    if (!tileTpls.length) return;

    var cardKeys = {};
    tileTpls.forEach(function (tpl) {
      var a = tpl.content.querySelector('a[href]');
      if (!a) return;
      cardKeys['t:' + normTitle(a.textContent)] = true;
      var u = normUrl(a.getAttribute('href'));
      if (u) cardKeys['u:' + u] = true;
    });
    Array.prototype.slice.call(menuSlot.children).forEach(function (li) {
      if (li.querySelector('ul')) return; // group with its own list — leave alone
      var a = li.querySelector('a[href]');
      if (!a) return;
      var u = normUrl(a.getAttribute('href'));
      if (cardKeys['t:' + normTitle(a.textContent)] || (u && cardKeys['u:' + u])) li.remove();
    });

    var frag = heroTpl.content.cloneNode(true);
    var grid = frag.querySelector('[data-navdrawer-slot]');
    var more = frag.querySelector('.menu-drawer__hero-more');
    if (!grid) return;
    var limit = parseInt(heroPane.getAttribute('data-navdrawer-hero-limit'), 10) || 0;
    tileTpls.forEach(function (tpl, i) {
      var f = tpl.content.cloneNode(true);
      var li = f.querySelector('li');
      if (li && limit && i >= limit) li.hidden = true;
      grid.appendChild(f);
    });
    if (more) {
      if (limit && tileTpls.length > limit) more.hidden = false;
      else more.remove();
    }
    menuSlot.appendChild(frag);
  }

  // Show more: reveal the hidden tiles of the grid the button controls,
  // then hand focus to the first newly revealed tile WITHOUT scrolling —
  // the tiles appear in place below the first 10; a scroll-into-view
  // here made the whole list jump up (BB feedback).
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.menu-drawer__hero-more');
    if (!btn) return;
    var grid = document.getElementById(btn.getAttribute('aria-controls'));
    if (!grid) return;
    var first = null;
    grid.querySelectorAll('li[hidden]').forEach(function (li) {
      li.hidden = false;
      if (!first) first = li.querySelector('a');
    });
    btn.setAttribute('aria-expanded', 'true');
    btn.hidden = true;
    if (first) {
      try { first.focus({ preventScroll: true }); } catch (err) { first.focus(); }
    }
  });

  // Dedupe keys: titles compared case-insensitively with whitespace and
  // zero-width characters removed; URLs compared as site-relative paths
  // (absolute storefront URLs, shopify:// handles and relative paths all
  // normalize to the same key), ignoring query/hash and trailing slash.
  function normTitle(text) {
    return String(text || '').replace(/[\s\u200b\u200c\u200d\ufeff]+/g, '').toUpperCase();
  }
  function normUrl(href) {
    var u = String(href || '').trim();
    if (!u || u === '#') return '';
    u = u.replace(/^shopify:\/\//, '/');
    u = u.replace(/^https?:\/\/[^\/]+/i, '');
    u = u.split('#')[0].split('?')[0];
    if (u.length > 1) u = u.replace(/\/+$/, '');
    return u.toLowerCase();
  }

  function stitchAll() {
    document.querySelectorAll('[data-navdrawer-root]').forEach(function (root) {
      if (root.getAttribute('data-navdrawer-root') === 'done') return;
      if (stitchRoot(root)) root.setAttribute('data-navdrawer-root', 'done');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stitchAll);
  } else {
    stitchAll();
  }

  // The header section can be re-rendered (Section Rendering API second
  // pass), replacing a stitched drawer with a fresh pending one. Watch
  // for that and restitch. Cheap: the callback only scans when nodes
  // were added, and stitchAll no-ops on already-done roots.
  var scheduled = false;
  new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      stitchAll();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Belt-and-suspenders: any interaction with the drawer trigger gets a
  // synchronous stitch attempt before the drawer becomes visible.
  document.addEventListener('pointerdown', function (e) {
    if (e.target && e.target.closest && e.target.closest('.header__icon--menu, header-drawer')) stitchAll();
  }, true);
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.header__icon--menu, header-drawer')) stitchAll();
  }, true);
})();
